import { apiClient } from './apiClient';

export interface WaitlistEntry {
  id: string;
  name: string;
  email: string;
  referral_code: string;
  waitlist_position: number;
  referral_count: number;
  is_active: boolean;
  email_confirmed_at?: Date;
  last_email_sent_at?: Date;
  created_at: Date;
  updated_at: Date;
  referred_by_id?: string;
  referrals?: WaitlistEntry[];
}

export interface WaitlistStats {
  total_entries: number;
  active_entries: number;
  top_referrers: Array<{
    name: string;
    email: string;
    referral_count: number;
    referral_code: string;
  }>;
}

export interface WaitlistFilters {
  search?: string;
  is_active?: boolean;
  has_referrals?: boolean;
  email_confirmed?: boolean;
  page?: number;
  limit?: number;
}

export class WaitlistService {
  // Get waitlist statistics
  static async getWaitlistStats(): Promise<WaitlistStats> {
    const response = await apiClient.get('/waitlist/stats');
    return response.data.data;
  }

  // Get all waitlist entries with optional filters
  static async getWaitlistEntries(filters: WaitlistFilters = {}): Promise<{
    entries: WaitlistEntry[];
    total: number;
    page: number;
    limit: number;
  }> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters.has_referrals !== undefined) params.append('has_referrals', filters.has_referrals.toString());
    if (filters.email_confirmed !== undefined) params.append('email_confirmed', filters.email_confirmed.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`/waitlist/entries?${params.toString()}`);
    return response.data.data;
  }

  // Get waitlist entry by ID
  static async getWaitlistEntry(id: string): Promise<WaitlistEntry> {
    const response = await apiClient.get(`/waitlist/entries/${id}`);
    return response.data.data;
  }

  // Get referrals by user ID
  static async getReferralsByUserId(userId: string): Promise<WaitlistEntry[]> {
    const response = await apiClient.get(`/waitlist/referrals/${userId}`);
    return response.data.data;
  }

  // Update waitlist entry
  static async updateWaitlistEntry(id: string, data: Partial<WaitlistEntry>): Promise<WaitlistEntry> {
    const response = await apiClient.put(`/waitlist/entries/${id}`, data);
    return response.data.data;
  }

  // Delete waitlist entry
  static async deleteWaitlistEntry(id: string): Promise<void> {
    await apiClient.delete(`/waitlist/entries/${id}`);
  }

  // Resend confirmation email
  static async resendConfirmationEmail(email: string): Promise<{ message: string }> {
    const response = await apiClient.post('/waitlist/resend-email', { email });
    return response.data;
  }

  // Export waitlist data
  static async exportWaitlistData(filters: WaitlistFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters.has_referrals !== undefined) params.append('has_referrals', filters.has_referrals.toString());
    if (filters.email_confirmed !== undefined) params.append('email_confirmed', filters.email_confirmed.toString());

    const response = await apiClient.get(`/waitlist/export?${params.toString()}`, {
      responseType: 'blob'
    });
    return response.data;
  }
} 